import { getGlobalHeadersAsObject } from "./global-headers";

export interface QueueItemInfo {
	model: string;
	table: string;
	id: number;
	person_id?: number | null;
	message_type?: string | null;
	queue_type?: string | null;
	deleted?: boolean;
}

export interface PluginWorkLog {
	id: number;
	created: string;
	completed: string | null;
	short_name: string | null;
	command_type: string | null;
	command_data: Record<string, unknown>;
	result_data: Record<string, unknown> | null;
	http_status: number | null;
	error_message: string | null;
	duration_seconds: number | null;
	is_success: boolean;
	extracted_data: Record<string, unknown> | null;
	ignore_safety: boolean;
	sent_at: string | null; // When work was sent to plugin. null=still queued
	acknowledged: string | null; // When an admin dismissed this error/stuck row
	queue_item_info: QueueItemInfo | null;
}

export interface PluginWorkLogFilters {
	command_type?: string | string[];
	status?: string;
	user?: string | string[];
	is_error?: boolean;
	is_stuck?: boolean;
	has_result?: boolean;
	is_sent?: boolean;
	days?: number;
	ordering?: string;
	limit?: number;
	offset?: number;
	command_data_search?: string;
}

export interface PaginatedPluginWorkLogResponse {
	count: number;
	next: string | null;
	previous: string | null;
	results: PluginWorkLog[];
}

/**
 * Fetch plugin work logs with optional filters and pagination.
 * Returns paginated response with metadata.
 */
export async function getPluginWorkLogs(
	filters?: PluginWorkLogFilters
): Promise<PaginatedPluginWorkLogResponse> {
	const params = new URLSearchParams();

	if (filters?.command_type) {
		const value = Array.isArray(filters.command_type)
			? filters.command_type.join(",")
			: filters.command_type;
		if (value) params.append("command_type", value);
	}
	if (filters?.status) params.append("status", filters.status);
	if (filters?.user) {
		const value = Array.isArray(filters.user)
			? filters.user.join(",")
			: filters.user;
		if (value) params.append("user", value);
	}
	if (filters?.is_error !== undefined)
		params.append("is_error", filters.is_error.toString());
	if (filters?.is_stuck !== undefined)
		params.append("is_stuck", filters.is_stuck.toString());
	if (filters?.has_result !== undefined)
		params.append("has_result", filters.has_result.toString());
	if (filters?.is_sent !== undefined)
		params.append("is_sent", filters.is_sent.toString());
	if (filters?.days) params.append("days", filters.days.toString());
	if (filters?.ordering) params.append("ordering", filters.ordering);
	if (filters?.limit) params.append("limit", filters.limit.toString());
	if (filters?.offset) params.append("offset", filters.offset.toString());
	if (filters?.command_data_search)
		params.append("command_data_search", filters.command_data_search);

	const queryString = params.toString();
	const url = `/api/v2/plugin-work-log/${queryString ? `?${queryString}` : ""}`;

	const response = await fetch(url, {
		method: "GET",
		headers: getGlobalHeadersAsObject(),
	});

	if (!response.ok) {
		throw new Error(`Failed to fetch plugin work logs: ${response.statusText}`);
	}

	const data = await response.json();

	// Handle DRF paginated response format
	if (Array.isArray(data)) {
		// Non-paginated response (shouldn't happen with pagination enabled)
		return {
			count: data.length,
			next: null,
			previous: null,
			results: data,
		};
	}

	// DRF pagination wraps results in {results: [...], count: X, next, previous}
	return data;
}

/**
 * Fetch a single plugin work log by ID.
 */
export async function getPluginWorkLogById(id: number): Promise<PluginWorkLog> {
	const response = await fetch(`/api/v2/plugin-work-log/${id}/`, {
		method: "GET",
		headers: getGlobalHeadersAsObject(),
	});

	if (!response.ok) {
		throw new Error(`Failed to fetch plugin work log: ${response.statusText}`);
	}

	return await response.json();
}

/**
 * Bulk-acknowledge plugin-work-log rows in a banner bucket. Returns the
 * number of rows newly acknowledged. Both buckets scope to the last 24h.
 */
export async function acknowledgePluginWorkLogBucket(
	bucket: "errors" | "stuck"
): Promise<{ acknowledged: number }> {
	const res = await fetch("/api/v2/plugin-work-log/acknowledge/", {
		method: "POST",
		headers: {
			...getGlobalHeadersAsObject(),
			"Content-Type": "application/json",
		},
		body: JSON.stringify({ bucket }),
	});
	if (!res.ok) {
		const data = await res.json().catch(() => ({}));
		throw new Error(data.error || "Failed to acknowledge plugin work log bucket");
	}
	return res.json();
}

/**
 * Filterable command types (command_data.command.command_type) and their labels.
 */
export const COMMAND_TYPE_LABELS: Record<string, string> = {
	send_invite: "Send Invitation",
	withdraw_invitation: "Withdraw Invitation",
	send_message: "Send Message",
	fetch_connections: "Fetch Connections",
	fetch_connection_count: "Fetch Connection Count",
	fetch_contactinfo: "Fetch Contact Info",
	fetch_inmail_cost: "Fetch InMail Cost",
	check_inmail_cost_for_message: "Check InMail Cost for Message",
	conversation_list: "Conversation List",
	messages_by_sync_token: "Messages by Sync Token",
	messages_by_anchor_timestamp: "Messages by Anchor Timestamp",
	messages_by_conversation: "Messages by Conversation",
};

/**
 * Get human-readable command type label.
 */
export function getCommandTypeLabel(commandType: string): string {
	return COMMAND_TYPE_LABELS[commandType] || commandType;
}

/**
 * Get human-readable status label.
 */
export function getStatusLabel(status: string): string {
	const labels: Record<string, string> = {
		assigned: "Assigned",
		success: "Success",
		failed: "Failed",
		timeout: "Timeout",
	};
	return labels[status] || status;
}

/**
 * Get MUI color for status.
 */
export function getStatusColor(
	status: string
): "default" | "success" | "error" | "warning" | "info" {
	const colors: Record<string, "default" | "success" | "error" | "warning" | "info"> =
		{
			assigned: "info",
			success: "success",
			failed: "error",
			timeout: "warning",
		};
	return colors[status] || "default";
}

/**
 * Delete a plugin work log by ID.
 */
export async function deletePluginWorkLog(id: number): Promise<void> {
	const response = await fetch(`/api/v2/plugin-work-log/${id}/`, {
		method: "DELETE",
		headers: getGlobalHeadersAsObject(),
	});

	if (!response.ok) {
		throw new Error(`Failed to delete plugin work log: ${response.statusText}`);
	}
}

/**
 * Acknowledge a single plugin work log row. Sets `acknowledged = now` so
 * it stops counting in the banner and disappears from error/stuck filters.
 */
export async function acknowledgePluginWorkLogRow(id: number): Promise<PluginWorkLog> {
	const response = await fetch(`/api/v2/plugin-work-log/${id}/`, {
		method: "PATCH",
		headers: {
			...getGlobalHeadersAsObject(),
			"Content-Type": "application/json",
		},
		body: JSON.stringify({ acknowledged: new Date().toISOString() }),
	});

	if (!response.ok) {
		throw new Error(
			`Failed to acknowledge plugin work log: ${response.statusText}`
		);
	}

	return await response.json();
}

/**
 * Toggle ignore_safety flag for a plugin work log.
 * When true, security_check() will bypass rate limiting for this bot.
 */
export async function toggleIgnoreSafety(
	id: number,
	ignore_safety: boolean
): Promise<PluginWorkLog> {
	const response = await fetch(`/api/v2/plugin-work-log/${id}/`, {
		method: "PATCH",
		headers: {
			...getGlobalHeadersAsObject(),
			"Content-Type": "application/json",
		},
		body: JSON.stringify({ ignore_safety }),
	});

	if (!response.ok) {
		throw new Error(`Failed to update ignore_safety flag: ${response.statusText}`);
	}

	return await response.json();
}

export interface ReplayResult {
	replayed: boolean;
	work_log_id: number;
	command_type: string | null;
	continuation_response: Record<string, unknown> | null;
}

/**
 * Replay a work log's result_data through the handler as if the plugin just sent it.
 * Useful for debugging and re-running handlers without waiting for plugin to resend.
 */
export async function replayWorkLog(id: number): Promise<ReplayResult> {
	const response = await fetch(`/api/v2/plugin-work-log/${id}/replay/`, {
		method: "POST",
		headers: getGlobalHeadersAsObject(),
	});

	if (!response.ok) {
		const errorData = await response.json().catch(() => ({}));
		throw new Error(
			errorData.error || `Failed to replay work log: ${response.statusText}`
		);
	}

	return await response.json();
}

/**
 * Fetch distinct usernames from work logs for filter dropdown.
 */
export async function getPluginWorkLogUsers(): Promise<string[]> {
	const response = await fetch("/api/v2/plugin-work-log/users/", {
		method: "GET",
		headers: getGlobalHeadersAsObject(),
	});

	if (!response.ok) {
		throw new Error(`Failed to fetch users: ${response.statusText}`);
	}

	return await response.json();
}
